import React, { useEffect, useState } from "react"

import { RefinementListProvided } from "react-instantsearch-core"
import { connectRefinementList } from "react-instantsearch-dom"
import { FormattedMessage, useIntl } from "react-intl"

import { Body, Button } from "~/components/ui"
import Checkbox from "~/components/ui/Checkbox/Checkbox"
import useIngredients from "~/hooks/useIngredients"
import { Category } from "~/models/Category"
import { sortRefinementListFacetsByLabel } from "~/utils/algoliaUtils"
import { capitalizeFirstLetter } from "~/utils/string"

import css from "./StaredRefinmentList.module.scss"

type propType = RefinementListProvided & {
  baseItems: any[]
  limit: number
  showMore: boolean
  category?: Category
}

const RefinementList = ({
  items,
  refine,
  createURL,
  baseItems = [],
  limit,
  showMore,
  category,
}: propType) => {
  const { getIngredientBySlug } = useIngredients()
  const [showingMore, setShowingMore] = useState(false)
  const [displayedItems, setDisplayedItems] = useState(
    baseItems.slice(0, limit) ?? items ?? []
  )
  const [showMoreItems, setShowMoreItems] = useState(
    baseItems.sort(sortRefinementListFacetsByLabel) ?? items ?? []
  )

  useEffect(() => {
    if (items.length > 0) {
      const filteredByCountItems = items.filter(item => {
        const ingName = getIngredientBySlug(item.label)
        return (
          item.count >= 5 ||
          ingName?.name.includes("®") || //IF THE INGREDIENT NAME INCLUDE ® OR ©, IT CAN BE A PARTNERSHIP, SO WE DISPLAY IT
          ingName?.name.includes("©") ||
          ingName?.name.includes("®") ||
          ingName?.name.includes("©") ||
          item.isRefined
        )
      })

      const orderedItems = [...filteredByCountItems].sort(
        sortRefinementListFacetsByLabel
      )

      const itemWithOrderIndex = filteredByCountItems.map(item => ({
        ...item,
        order: orderedItems.indexOf(item),
      }))

      setDisplayedItems(
        itemWithOrderIndex.slice(0, limit).sort(sortRefinementListFacetsByLabel)
      )
      setShowMoreItems(
        itemWithOrderIndex.slice(limit).sort(sortRefinementListFacetsByLabel)
      )
    }
  }, [items])

  return displayedItems.length ? (
    <div className={css.refinmentList}>
      <div className={css.ingredientList}>
        <IngredientCheckableList
          items={displayedItems}
          refine={refine}
          createURL={createURL}
          itemClassName={css.star}
          category={category}
        />
        <IngredientCheckableList
          items={showMoreItems}
          refine={refine}
          createURL={createURL}
          itemClassName={showingMore ? css.showingMore : css.showingLess}
          category={category}
        />
      </div>

      {showMore && displayedItems.length < showMoreItems.length ? (
        <Button
          variant="tertiary"
          underline
          uppercase={false}
          color="original"
          onClick={() => setShowingMore(!showingMore)}
        >
          <FormattedMessage
            id={
              showingMore
                ? "category/text:filter-ingredients-see-less"
                : "category/text:filter-ingredients-see-more"
            }
          />
        </Button>
      ) : null}
    </div>
  ) : null
}

const IngredientCheckableList = ({
  items,
  refine,
  createURL,
  itemClassName,
  category,
}: {
  items: {
    label: string
    value: string[]
    count: number
    isRefined: boolean
    order?: number
  }[]
  refine: (value: string[]) => any
  createURL: (...args: any[]) => any
  itemClassName?: string
  category?: Category
}) => {
  const intl = useIntl()

  const { getIngredientBySlug } = useIngredients()

  const getHrefValue = (value: string) => {
    let baseUrl = `/${intl.locale}${createURL(value)}`
    if (category?.slug) {
      baseUrl = baseUrl.replace(
        `/${intl.formatMessage({ id: "path:recipes" })}/`,
        `/${category.slug}/`
      )
    }
    return baseUrl
  }

  return (
    <>
      {items.map((item: any) => {
        return (
          <div
            className={itemClassName}
            key={item.label + item.isRefined}
            style={{ order: item.order }}
          >
            <Checkbox
              label={item.label}
              value={item.value}
              defaultChecked={item.isRefined}
              onclick={event => {
                event.preventDefault()
                refine(item.value)
                createURL(item.value)
              }}
              disabled={item.count === 0}
              href={getHrefValue(item.value)}
              labelProps={{
                title: `${intl.formatMessage({
                  id: "text:recipes",
                })} ${capitalizeFirstLetter(
                  getIngredientBySlug(item.label)?.name ?? item.label ?? ""
                )}`,
                onClick: (event: any) => {
                  event.preventDefault()
                  refine(item.value)
                  createURL(item.value)
                },
              }}
            >
              <Body
                variant="body2"
                semiBold
                color={item.count === 0 ? "gray4" : "black"}
              >
                {capitalizeFirstLetter(
                  getIngredientBySlug(item.label)?.name ?? item.label ?? ""
                )}{" "}
                ({item.count})
              </Body>
            </Checkbox>
          </div>
        )
      })}
    </>
  )
}

const StaredRefinmentList = connectRefinementList<propType>(RefinementList)

export default StaredRefinmentList
